import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicAPI } from '../api';
import { FileText, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { BrandLogo } from '../components/Brand';
import { FormFields, ErrorSummary, validateFields, focusField } from '../components/FormFields';
import { SIGNED_IN_PRODUCT_NAME } from '../config/brand';

const PublicForm = () => {
  const { shareId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setLoading(true);
    PublicAPI.getForm(shareId)
      .then((f) => { setForm(f); setError(''); })
      .catch((e) => setError(e.response?.status === 404 ? 'This form does not exist or has been removed.' : 'Something went wrong.'))
      .finally(() => setLoading(false));
  }, [shareId]);

  const update = (label, val) => {
    const next = { ...values, [label]: val };
    setValues(next);
    if (attempted) setErrors(validateFields(form.fields, next));
  };

  const handleSubmit = async () => {
    setAttempted(true);
    const clientErrors = validateFields(form.fields, values);
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length) { focusField(form.fields.find((f) => clientErrors[f.label]).label); return; }
    setSubmitting(true);
    try {
      await PublicAPI.submit(shareId, values);
      setSubmitted(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && detail?.errors) {
        setErrors(detail.errors);
        focusField(Object.keys(detail.errors)[0]);
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setSubmitted(false); setValues({}); setErrors({}); setAttempted(false); };

  if (loading) return <div className="min-h-screen bg-[#F5F6FB] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#0F766E] animate-spin" /></div>;

  if (error && !form) {
    return (
      <div className="min-h-screen bg-[#F5F6FB] flex flex-col items-center justify-center p-6 text-center" data-testid="public-form-unavailable">
        <div className="w-16 h-16 rounded-full bg-[#FEF2F2] flex items-center justify-center mb-4"><FileText className="w-7 h-7 text-[#E5387B]" /></div>
        <h1 className="text-[22px] font-bold text-[#0A1551]">Form unavailable</h1>
        <p className="text-[14px] text-[#6F76A7] mt-1 max-w-sm">{error}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F6FB] flex flex-col items-center justify-center p-6 text-center" data-testid="public-form-success">
        <div className="w-20 h-20 rounded-full bg-[#E6F7EE] flex items-center justify-center mb-4"><CheckCircle2 className="w-10 h-10 text-[#0AA24F]" strokeWidth={2} /></div>
        <h1 className="text-[26px] font-bold text-[#0A1551]">Thanks for your submission!</h1>
        <p className="text-[15px] text-[#6F76A7] mt-2 max-w-sm">Your response to &quot;{form.name}&quot; has been recorded.</p>
        <button onClick={reset} className="mt-6 px-5 py-2.5 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[14px] font-semibold">Submit another response</button>
      </div>
    );
  }

  const errorCount = Object.keys(errors).length;

  return (
    <div className="min-h-screen bg-[#F5F6FB]" data-testid="public-form">
      <header className="bg-white border-b border-[#E3E5F5]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo size="sm" />
          <span className="text-[12px] text-[#6F76A7]">Powered by {SIGNED_IN_PRODUCT_NAME}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <form className="bg-white border border-[#E3E5F5] rounded-2xl shadow-sm overflow-hidden" noValidate onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="h-2 bg-[#0F766E]"></div>
          <div className="p-6 md:p-10">
            <h1 className="text-[26px] md:text-[30px] font-bold text-[#0A1551] leading-tight">{form.name}</h1>
            {form.description && <p className="text-[15px] text-[#6F76A7] mt-2">{form.description}</p>}
            {form.status === 'Disabled' && <p className="mt-4 text-[13px] text-[#B7791F] bg-[#FFF7E0] rounded-md px-3 py-2" data-testid="form-disabled-notice">This form is not accepting submissions right now.</p>}

            <div className="mt-8">
              <FormFields fields={form.fields} values={values} onChange={update} errors={errors} />
            </div>

            <ErrorSummary errors={errors} fields={form.fields} onReview={focusField} />
            {error && <p className="mt-4 text-[13px] text-[#E5387B]" role="alert">{error}</p>}

            <button
              type="submit"
              disabled={submitting || form.status === 'Disabled'}
              data-testid="public-form-submit"
              aria-describedby={errorCount ? 'error-summary' : undefined}
              className="mt-6 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[15px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit
            </button>
            <p className="text-center text-[12px] text-[#9599C0] mt-3">Fields marked * are required. You can&apos;t submit until every required item is complete.</p>
          </div>
        </form>
        <p className="text-center text-[12px] text-[#6F76A7] mt-6">Never submit passwords through this form.</p>
      </main>
    </div>
  );
};

export default PublicForm;
